import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, inject } from '@angular/core';
import * as i1 from '@ngrx/store';
import { select, combineReducers, createAction, props, createReducer, on, createSelector, Store } from '@ngrx/store';
import { throwError, pipe, mergeMap, map, catchError as catchError$1, of, finalize as finalize$1 } from 'rxjs';
import { tap, catchError, finalize } from 'rxjs/operators';
import { get } from 'lodash';
import { createEntityAdapter } from '@ngrx/entity';
import { ofType } from '@ngrx/effects';

class StoreService {
    constructor(store, selectors) {
        this.store = store;
        // Iben: If the selectors are provided, we create an object that will create an object with selectors for each slice in the state
        if (selectors) {
            this.stateWrapper = Object.keys(selectors).reduce((previous, key) => {
                return {
                    ...previous,
                    [`${key}$`]: this.selectFromStore(selectors[key]),
                    [`${key}Loading$`]: this.selectLoadingFromStore(selectors[key]),
                    [`${key}Error$`]: this.selectErrorFromStore(selectors[key]),
                    [`${key}ErrorMessage$`]: this.selectErrorMessageFromStore(selectors[key]),
                };
            }, {});
        }
    }
    /**
     * Select the data of a store slice
     *
     * @template StoreItem - The interface of the selected item/items
     * @param selector - The selector object
     */
    selectFromStore(selector) {
        return this.store.pipe(select(selector.select || selector.selectAll));
    }
    /**
     * Select the loading state of a store slice
     *
     * @param selector - The selector object
     */
    selectLoadingFromStore(selector) {
        return this.store.pipe(select(selector.loading || selector.selectLoading));
    }
    /**
     * Select the error state of a store slice
     *
     * @param selector - The selector object
     */
    selectErrorFromStore(selector) {
        return this.store.pipe(select(selector.error || selector.selectError));
    }
    /**
     * Select the error message state of a store slice
     *
     * @param selector - The selector object
     */
    selectErrorMessageFromStore(selector) {
        return this.store.pipe(select(selector.errorMessage || selector.selectErrorMessage));
    }
    /**
     * An object that holds all the state selectors of the provided store slice.
     *
     * The generated selectors need to be provided to the constructor of the service in order for this object not to be undefined.
     */
    get state() {
        // Iben: If no selectors were provided, we throw an error informing the user
        if (!this.stateWrapper) {
            console.error('NgxStore: No selectors were provided to the constructor of the StoreService extender. Without it, the state object cannot be created and will result in an error.');
            return undefined;
        }
        // Iben: Return the stateWrapper
        return this.stateWrapper;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StoreService, deps: [{ token: i1.Store }, { token: 'selectors', optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StoreService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.0.3", ngImport: i0, type: StoreService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.Store }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['selectors']
                }] }] });

/**
 * Dispatches data to the store based on a provided action and Observable. Loading and error state will be handled by default.
 *
 * @param dispatchAction - The action we wish to use to dispatch data to the store
 * @param data - The data we wish to dispatch to the store
 * @param store - The store we wish to dispatch the data to
 * @param dispatchType - Whether we wish to set, add or update the data. By default this is `set`
 * @return {*}  {Observable<DataType>}
 */
const dispatchDataToStore = (dispatchAction, data, store, dispatchType = 'set') => {
    // Iben: Set the loading state to true and the error state to false to start a new set
    store.dispatch(dispatchAction.loading({ payload: true }));
    store.dispatch(dispatchAction.error({ payload: false }));
    // Iben: Set, add or update the data according to the provided dispatch type
    return data.pipe(tap((payload) => {
        if (dispatchType === 'set') {
            store.dispatch(dispatchAction.set({ payload }));
        }
        else if (dispatchType === 'add') {
            store.dispatch(dispatchAction.add({ payload }));
        }
        else {
            store.dispatch(dispatchAction.update({ payload }));
        }
    }), 
    // Iben: Catch the error and dispatch it to the store
    catchError((err) => {
        store.dispatch(dispatchAction.error({ payload: err }));
        return throwError(() => err);
    }), 
    // Iben: Set the loading state to false
    finalize(() => {
        store.dispatch(dispatchAction.loading({ payload: false }));
    }));
};

/**
 * Generates selectors,actions and reducers for a store
 *
 * @template SliceKeys - The keys of the Store slices
 * @template ResultType - The typing of the store slice
 * @param slice - The slice for which you want to generate store assets
 * @param options - The subSlices you want to generate store assets for
 */
const createStoreAssets = (slice, options) => {
    // Iben: Generate the assets
    const storeAssets = [...options].reduce((result, current) => {
        return {
            ...result,
            [current.subSlice]: current.selectId
                ? current.generator({
                    slice: `${slice}.${current.subSlice.toString()}`,
                    selectId: current.selectId,
                    initialStateValue: current.initialValue,
                })
                : current.generator({
                    slice: `${slice}.${current.subSlice.toString()}`,
                    initialStateValue: current.initialValue,
                }),
        };
    }, {});
    // Iben: Create base objects for our result
    const selectors = {};
    const actions = {};
    // Iben: Map the assets to the right entries
    Object.entries(storeAssets).forEach(([key, assets]) => {
        selectors[key] = assets.selectors;
        actions[key] = assets.actions;
    });
    // Iben: Generate a combined reducer
    const reducers = combineReducers(Object.keys(storeAssets).reduce((previous, current) => {
        return {
            ...previous,
            [current]: storeAssets[current]?.reducers,
        };
    }, {}));
    // Iben: Return the result
    return {
        selectors,
        actions,
        reducers,
    };
};

/**
 * Creates store assets to save basic properties (object, string, number, etc.) into the store
 *
 * @param slice - The slice we wish to save the data in
 */
const createBaseStoreAssets = ({ slice, initialStateValue, }) => {
    // Iben: Create actions
    const actions = {
        set: createAction(`[${slice}]: Set`, props()),
        loading: createAction(`[${slice}]: Loading`, props()),
        error: createAction(`[${slice}]: Error`, props()),
        clear: createAction(`[${slice}]: Clear`),
        effects: {
            set: createAction(`[${slice}]: Trigger set`, props()),
        },
    };
    // Iben: Set the initial state
    const initialState = {
        data: initialStateValue,
        loading: false,
        error: false,
        errorMessage: null,
    };
    // Iben: Create reducers
    const reducers = createReducer(initialState, on(actions.set, (state, { payload }) => {
        return { ...state, data: payload };
    }), on(actions.loading, (state, { payload }) => {
        return { ...state, loading: payload };
    }), on(actions.error, (state, { payload }) => {
        if (typeof payload === 'boolean') {
            return { ...state, error: payload, errorMessage: null };
        }
        return { ...state, error: true, errorMessage: payload.error };
    }), on(actions.clear, () => initialState), on(actions.effects.set, (state) => {
        return {
            ...state,
            loading: true,
            error: false,
            errorMessage: undefined,
        };
    }));
    // Iben: Create selectors
    const featureSelector = (state) => get(state, slice);
    const selectors = {
        select: createSelector(featureSelector, (state) => state.data),
        selectLoading: createSelector(featureSelector, (state) => state?.loading),
        selectError: createSelector(featureSelector, (state) => state?.error),
        selectErrorMessage: createSelector(featureSelector, (state) => state?.errorMessage),
    };
    return { actions, reducers, selectors };
};

/**
 * Creates basic reducers and selectors for a store slice
 *
 * @param slice - The slice of the store
 * @param actions - The actions used to interact with the store slice
 * @param selectId - An optional match function of the identifier of the payload is not "id"
 */
const createEntityAdapterStoreAssets = ({ slice, selectId, initialStateValue, }) => {
    // Iben: Fill in unprovided actions
    const actions = {
        add: createAction(`[${slice}]: Add`, props()),
        set: createAction(`[${slice}]: Set`, props()),
        update: createAction(`[${slice}]: Update`, props()),
        delete: createAction(`[${slice}]: Delete`, props()),
        loading: createAction(`[${slice}]: Loading`, props()),
        error: createAction(`[${slice}]: Error`, props()),
        clear: createAction(`[${slice}]: Clear`),
        effects: {
            add: createAction(`[${slice}]: Trigger add`, props()),
            set: createAction(`[${slice}]: Trigger set`, props()),
            update: createAction(`[${slice}]: Trigger update`, props()),
            delete: createAction(`[${slice}]: Trigger delete`, props()),
        },
    };
    // Iben: Create adapter
    const adapter = createEntityAdapter({
        ...(selectId ? { selectId } : {}),
    });
    // Iben: Create the initial state
    const initialState = adapter.getInitialState({
        ids: (initialStateValue || []).map((item) => {
            return selectId ? selectId(item) : item['id'];
        }),
        entities: (initialStateValue || []).reduce((previous, current) => {
            return {
                ...previous,
                [selectId ? selectId(current) : current['id']]: current,
            };
        }, {}),
        entityStatus: {
            loading: false,
            error: false,
            errorMessage: undefined,
        },
    });
    // Iben: Create state selectors
    const featureSelector = (state) => get(state, slice);
    const selectors = {
        selectAll: createSelector(featureSelector, adapter.getSelectors().selectAll),
        selectLoading: createSelector(featureSelector, (state) => state?.entityStatus.loading),
        selectError: createSelector(featureSelector, (state) => state?.entityStatus.error),
        selectErrorMessage: createSelector(featureSelector, (state) => state?.entityStatus.errorMessage),
    };
    // Iben: Create base reducer actions
    const reducers = createReducer(initialState, on(actions.clear, (state) => {
        adapter.removeAll(state);
    }), on(actions.add, (state, { payload }) => {
        return Array.isArray(payload)
            ? adapter.addMany(payload, state)
            : adapter.addOne(payload, state);
    }), on(actions.update, (state, { payload }) => {
        // Iben: Setup id and changes
        const id = payload['id'] || selectId(payload);
        const changes = payload;
        return adapter.updateOne({ id, changes }, state);
    }), on(actions.delete, (state, { payload }) => {
        // Iben: Get id
        const id = payload['id'] || selectId(payload);
        return adapter.removeOne(id, state);
    }), on(actions.set, (state, { payload }) => {
        return adapter.setAll(payload, state);
    }), on(actions.loading, (state, { payload }) => {
        return {
            ...state,
            entityStatus: {
                ...state.entityStatus,
                loading: payload,
            },
        };
    }), on(actions.error, (state, { payload }) => {
        // Iben: In case we pass a boolean, we don't add the error message
        if (typeof payload === 'boolean') {
            return {
                ...state,
                entityStatus: {
                    ...state.entityStatus,
                    error: payload,
                },
            };
        }
        // Iben: In case we pass anything but a boolean, we set the error to true and set the error message
        return {
            ...state,
            entityStatus: {
                ...state.entityStatus,
                error: true,
                errorMessage: payload,
            },
        };
    }), on(actions.effects.set, (state) => {
        return {
            ...state,
            entityStatus: { loading: true, error: false, errorMessage: undefined },
        };
    }), on(actions.effects.delete, (state) => {
        return {
            ...state,
            entityStatus: { loading: true, error: false, errorMessage: undefined },
        };
    }), on(actions.effects.add, (state) => {
        return {
            ...state,
            entityStatus: { loading: true, error: false, errorMessage: undefined },
        };
    }), on(actions.effects.add, (state) => {
        return {
            ...state,
            entityStatus: { loading: true, error: false, errorMessage: undefined },
        };
    }));
    // Iben: return the created reducers and selectors
    return {
        reducers,
        selectors,
        actions,
    };
};

/**
 *
 *
 * @template DataType - The datatype of the source
 * @template PayloadType - The optional type for the payload
 * @param actions The actions of the subslice
 * @param action - The action we wish to dispatch
 * @param source - The source of the data
 */
const handleEffect = (actions, action, source) => {
    // Iben: Inject the store for the finalize dispatch
    const store = inject(Store);
    return pipe(
    // Iben: Listen to the provided action
    ofType(actions.effects[action]), 
    // Iben: Merge-map to the request
    mergeMap((value) => {
        // Iben: Fetch the optional payload to the request
        return source(value?.payload).pipe(
        // Iben: Map the call to an action
        map((payload) => {
            return {
                type: actions[action].type,
                payload,
            };
        }), 
        // Iben: Map the error to the error action
        catchError$1(() => {
            return of({ type: actions.error.type, payload: true });
        }), 
        // Iben: Dispatch the loading in a finalize so that the current store setup can still work without effects
        // As this finalize is on the source, it will run this operator
        finalize$1(() => {
            store.dispatch(actions.loading({ payload: false }));
        }));
    }));
};

/*
 * Public API Surface of store
 */

/**
 * Generated bundle index. Do not edit.
 */

export { StoreService, createBaseStoreAssets, createEntityAdapterStoreAssets, createStoreAssets, dispatchDataToStore, handleEffect };
